import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { fetchAllFCServices } from '$api/evoAPIs'
import { App } from 'antd'
import { updateStatusAttributes } from '$utils'
import { useRefetchTimer } from '$context/RefetchTimerProvider'
import { evoClient, patchPart, fetchPart } from '$api/client'
import { useTranslation } from 'react-i18next' // Added for translations

export const FcUpdateParts = () => {
  const queryClient = useQueryClient()
  const { stopRefetch } = useRefetchTimer()
  const { message, notification } = App.useApp()
  const { t } = useTranslation() // gets the translation function

  let arrOfNotUpdatedIds
  try {
    arrOfNotUpdatedIds = JSON.parse(localStorage.getItem('notupdatedfc'))
  } catch {
    arrOfNotUpdatedIds = []
  }

  const updateClientPartStatus = useMutation(
    async (fcObject) => {
      const freshPart = await queryClient.fetchQuery({
        queryKey: ['part', fcObject.part.db_id_client],
        queryFn: fetchPart,
      })

      if (!freshPart) {
        console.warn(
          `FcUpdateParts: Could not fetch fresh part data for ID ${fcObject.part.db_id_client}. Aborting update.`,
        )
        throw new Error(`Part not found: ${fcObject.part.db_id_client}`)
      }

      const payload = {
        attributes: [...freshPart.attributes],
        status_attributes: [...freshPart.status_attributes],
        dwh_materials_fc_check: { ...freshPart.dwh_materials_fc_check },
      }

      const fcCheckResults = {}
      if (fcObject.results && Array.isArray(fcObject.results)) {
        fcObject.results.forEach((result) => {
          fcCheckResults[result.dwh_material_id] = result.is_feasible
        })
      }
      payload.dwh_materials_fc_check = fcCheckResults
      const isGenerallyFeasible = Object.values(fcCheckResults).some(
        (status) => status === true,
      )

      updateStatusAttributes(
        payload,
        'attributes',
        'Feasibility Check',
        isGenerallyFeasible.toString(),
      )
      updateStatusAttributes(
        payload,
        'attributes',
        'Technical Suitability',
        isGenerallyFeasible.toString(),
      )
      updateStatusAttributes(
        payload,
        'status_attributes',
        'Feasibility Check',
        '1',
      )

      return evoClient.patch(patchPart + freshPart.id, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['parts'])
        queryClient.invalidateQueries(['allparts'])
        queryClient.invalidateQueries(['partKpis'])
        queryClient.invalidateQueries(['bookedServices'])
        queryClient.invalidateQueries(['orderPartsLists'])

        const remaining = JSON.parse(
          localStorage.getItem('notupdatedfc') || '[]',
        )

        if (!remaining || remaining.length === 0) {
          notification.success({
            message: t('fc_update_parts.success_message'),
            description: t('fc_update_parts.success_description'),
            placement: 'top',
          })
          localStorage.setItem('notupdatedfc', '')
          stopRefetch()
        }
      },
      onError: (err) => {
        message.error(
          `${err.response?.status || 'Error'}: ${t('fc_update_parts.error_description')}`,
        )
      },
    },
  )

  useQuery(['feasibilitycheck'], fetchAllFCServices, {
    //enabled: typeof bsData !== "undefined",
    refetchInterval: () => {
      try {
        const ids = JSON.parse(localStorage.getItem('notupdatedfc') || '[]')
        return ids.length > 0 ? 3000 : false
      } catch {
        return false
      }
    },
    onSuccess: (data) => {
      let pendingIds = []
      try {
        pendingIds = JSON.parse(localStorage.getItem('notupdatedfc') || '[]')
      } catch {
        pendingIds = []
      }

      if (!pendingIds || pendingIds.length === 0) return

      // Filter only those parts that have IDs matching the not updated IDs
      const fcObjectsData = data.filter((fcObject) =>
        pendingIds.includes(fcObject.id),
      )

      const readyToUpdate = []
      const stillPending = []

      fcObjectsData.forEach((fcObject) => {
        const hasResults =
          fcObject.results &&
          Array.isArray(fcObject.results) &&
          fcObject.results.length > 0 &&
          fcObject.results.every(
            (r) => r.is_feasible !== null && r.is_feasible !== undefined,
          )

        if (hasResults) {
          readyToUpdate.push(fcObject)
        } else {
          stillPending.push(fcObject.id)
        }
      })

      const updatedIds = pendingIds.filter((id) => stillPending.includes(id))
      localStorage.setItem('notupdatedfc', JSON.stringify(updatedIds))

      // Only patch the ones which are ready
      readyToUpdate.forEach((fcObject) => {
        updateClientPartStatus.mutate(fcObject)
      })
    },
  })

  useEffect(() => {
    if (!arrOfNotUpdatedIds || arrOfNotUpdatedIds.length === 0) {
      stopRefetch()
    }
  })
}
